import {
  Box,
  Button,
  IconButton,
  Rating as MuiRating,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import {
  HiOutlineSpeakerWave,
  HiOutlineSpeakerXMark,
  HiSpeakerWave,
} from 'react-icons/hi2';
import { MdGraphicEq } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import Lottie from 'react-lottie';
import { useSnackbar } from 'notistack';
import {
  getSingleRestaurantDetails,
  submitCustomRating,
  submitRating,
} from '../../api';
import Loader from '../Components/Common/Loader';
import anime from '../../Assets/anime/reviewd.json';
import anime2 from '../../Assets/anime/sorrry.json';
import bgImg from '../../Assets/images/bg.png';

const ACCENT = '#bb162f';
const ACCENT_SOFT = '#FFF';

const CATEGORIES = [
  { key: 'overallRating', label: 'Overall Satisfaction' },
  { key: 'foodRating', label: 'Food Quality' },
  { key: 'serviceRating', label: 'Service & Hospitality' },
  { key: 'ambienceRating', label: 'Atmosphere & Ambience' },
  { key: 'valueForMoneyRating', label: 'Value for Money' },
];

const NOISE_OPTIONS = [
  { value: 1, label: 'Silent', icon: HiOutlineSpeakerXMark },
  { value: 2, label: 'Quiet', icon: HiOutlineSpeakerWave },
  { value: 3, label: 'Lively', icon: HiSpeakerWave },
  { value: 4, label: 'Loud', icon: MdGraphicEq },
];

const SENTIMENT_EMOJIS = ['😞', '😐', '🙂', '😊', '😍'];

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: '#FAFAFA',
    fontSize: '14px',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#D1D5DB' },
    '&.Mui-focused fieldset': {
      borderColor: ACCENT,
      borderWidth: '1px',
    },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SectionTitle = ({ children, action }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      mb: 2,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        sx={{
          width: 4,
          height: 22,
          borderRadius: '999px',
          bgcolor: ACCENT,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#111827' }}>
        {children}
      </Typography>
    </Box>
    {action}
  </Box>
);

const CategoryRow = ({ label, value, onChange, index }) => (
  <motion.div
    custom={index}
    variants={fadeUp}
    initial="hidden"
    animate="visible"
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        py: 1.75,
        borderBottom: '1px solid #F3F4F6',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: '13px', sm: '14px' },
          fontWeight: 600,
          color: '#374151',
          flex: 1,
          minWidth: 0,
        }}
      >
        {label}
      </Typography>
      <MuiRating
        value={value}
        onChange={(_, v) => onChange(v || 0)}
        precision={1}
        size="medium"
        sx={{
          color: ACCENT,
          '& .MuiRating-iconEmpty': {
            color: '#E5E7EB',
          },
          '& .MuiRating-iconFilled': {
            transition: 'transform 0.2s ease',
          },
          '& .MuiRating-icon:hover': {
            transform: 'scale(1.12)',
          },
        }}
      />
    </Box>
  </motion.div>
);

const ResultState = ({ variant, lottieOptions }) => {
  const isSorry = variant === 'reviewed';
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 }, px: 3 }}>
        <Lottie options={lottieOptions} width={200} height={200} />
        <Typography
          sx={{ fontWeight: 800, mt: 2, fontSize: '22px', color: '#111827' }}
        >
          {isSorry ? 'Already reviewed' : 'Thank you!'}
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            fontSize: '14px',
            color: '#6B7280',
            maxWidth: 320,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          {isSorry
            ? 'Your booking has already been reviewed. We appreciate your feedback.'
            : 'Your feedback helps us improve and deliver a better dining experience.'}
        </Typography>
        <Typography
          sx={{ mt: 4, fontSize: '11px', color: ACCENT, fontWeight: 600 }}
        >
          Powered by{' '}
          <a href="https://reisty.com" style={{ color: ACCENT }}>
            Reisty
          </a>
        </Typography>
      </Box>
    </motion.div>
  );
};

const Rating = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryParams = queryString.parse(location.search);
  const { BookingID, RestaurantID, GuestID } = queryParams;
  const isGuestReview = !BookingID && !GuestID;

  const [ratings, setRatings] = useState({
    foodRating: 0,
    overallRating: 0,
    valueForMoneyRating: 0,
    noiseLevelsRating: 0,
    serviceRating: 0,
    ambienceRating: 0,
  });
  const [comment, setComment] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [sentiment, setSentiment] = useState(80);
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const restaurantName =
    data?.RestaurantName || queryParams.RestaurantName || 'our restaurant';

  const sentimentEmoji = useMemo(() => {
    const idx = Math.min(
      SENTIMENT_EMOJIS.length - 1,
      Math.floor((sentiment / 100) * SENTIMENT_EMOJIS.length),
    );
    return SENTIMENT_EMOJIS[idx];
  }, [sentiment]);

  const guestDetailsValid =
    !isGuestReview ||
    (firstName.trim() &&
      lastName.trim() &&
      email.trim() &&
      isValidEmail(email));

  const canSubmit =
    ratings.overallRating > 0 &&
    ratings.noiseLevelsRating > 0 &&
    guestDetailsValid &&
    Boolean(RestaurantID) &&
    !isSubmitting;

  const handleRatingChange = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmitRatings = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setIsReviewed(false);
    setIsSuccessful(false);

    try {
      const res = isGuestReview
        ? await submitCustomRating({
            foodRating: ratings.foodRating,
            valueForMoneyRating: ratings.valueForMoneyRating,
            noiseLevelsRating: ratings.noiseLevelsRating,
            serviceRating: ratings.serviceRating,
            ambienceRating: ratings.ambienceRating,
            overallRating: ratings.overallRating,
            comment: comment.trim(),
            restaurantId: RestaurantID,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
          })
        : await submitRating(
            ratings.foodRating,
            ratings.valueForMoneyRating,
            ratings.noiseLevelsRating,
            ratings.serviceRating,
            ratings.ambienceRating,
            ratings.overallRating,
            comment.trim(),
            RestaurantID,
            BookingID,
            GuestID,
          );

      if (res?.data?.error_message === 'Booking already reviewed') {
        setIsReviewed(true);
      } else if (res?.data?.status) {
        setIsSuccessful(true);
      } else {
        enqueueSnackbar(res?.data?.error_message || 'Unable to submit review', {
          variant: 'error',
        });
      }
    } catch (err) {
      enqueueSnackbar(err?.message || 'Something went wrong', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!RestaurantID) {
      setIsFetching(false);
      return;
    }

    const fetchRestaurant = async () => {
      setIsFetching(true);
      try {
        const res = await getSingleRestaurantDetails(RestaurantID);
        setData(res?.data?.result?.[0] ?? null);
      } catch {
        enqueueSnackbar('Could not load restaurant details', {
          variant: 'warning',
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchRestaurant();
  }, [RestaurantID, enqueueSnackbar]);

  const lottieSuccess = {
    loop: true,
    autoplay: true,
    animationData: anime,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
  };

  const lottieSorry = {
    loop: true,
    autoplay: true,
    animationData: anime2,
    rendererSettings: { preserveAspectRatio: 'xMidYMid slice' },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: { xs: 'flex-end', sm: 'center' },
        justifyContent: 'center',
        px: { xs: 0, sm: 2 },
        py: { xs: 0, sm: 3 },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url('${bgImg}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'blur(14px)',
          transform: 'scale(1.08)',
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.45)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          display: 'flex',
          alignItems: { xs: 'flex-end', sm: 'center' },
          justifyContent: 'center',
        }}
      >
        {(isFetching || isSubmitting) && <Loader />}

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          style={{ width: '100%', maxWidth: 520 }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: { xs: '20px 20px 0 0', sm: '20px' },
              border: { xs: 'none', sm: '1px solid #E5E7EB' },
              boxShadow: {
                xs: '0 -8px 40px rgba(15, 23, 42, 0.08)',
                sm: '0 24px 60px rgba(15, 23, 42, 0.1)',
              },
              overflow: 'hidden',
              maxHeight: { xs: '96dvh', sm: 'none' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: { xs: 2.5, sm: 3 },
                pt: { xs: 2.5, sm: 3 },
                pb: 2,
                borderBottom: '1px solid #F3F4F6',
                position: 'relative',
              }}
            >
              <IconButton
                onClick={() => navigate(-1)}
                aria-label="Close"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 8,
                  color: '#9CA3AF',
                  '&:hover': { color: '#374151', bgcolor: '#F3F4F6' },
                }}
              >
                <IoClose size={22} />
              </IconButton>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 5 }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '20px', sm: '22px' },
                      color: '#111827',
                      lineHeight: 1.15,
                      fontFamily: 'Optima',
                    }}
                  >
                    Rate Your Experience
                  </Typography>
                  <Typography
                    sx={{ fontSize: '13px', color: '#9CA3AF', mt: 0.25 }}
                  >
                    At {restaurantName}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              className="hide_scrollbar"
              sx={{
                flex: 1,
                overflowY: 'auto',
                px: { xs: 2.5, sm: 3 },
                py: 3,
                pb: { xs: 2, sm: 3 },
              }}
            >
              <AnimatePresence mode="wait">
                {isReviewed ? (
                  <ResultState
                    key="reviewed"
                    variant="reviewed"
                    lottieOptions={lottieSorry}
                  />
                ) : isSuccessful ? (
                  <ResultState
                    key="success"
                    variant="success"
                    lottieOptions={lottieSuccess}
                  />
                ) : (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                  >
                    {isGuestReview && (
                      <motion.div
                        custom={0}
                        variants={fadeUp}
                        initial="hidden"
                        animate="visible"
                      >
                        <SectionTitle>Your Details</SectionTitle>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 1.5,
                            mb: 3,
                          }}
                        >
                          <TextField
                            label="First name"
                            required
                            fullWidth
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            sx={fieldSx}
                          />
                          <TextField
                            label="Last name"
                            required
                            fullWidth
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            sx={fieldSx}
                          />
                          <TextField
                            label="Email"
                            required
                            type="email"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={
                              Boolean(email.trim()) && !isValidEmail(email)
                            }
                            helperText={
                              email.trim() && !isValidEmail(email)
                                ? 'Enter a valid email address'
                                : ' '
                            }
                            sx={{
                              ...fieldSx,
                              gridColumn: { xs: 'auto', sm: '1 / -1' },
                            }}
                          />
                        </Box>
                      </motion.div>
                    )}

                    {/* Category ratings */}
                    <SectionTitle>Category Ratings</SectionTitle>
                    <Box sx={{ mb: 3 }}>
                      {CATEGORIES.map((cat, index) => (
                        <CategoryRow
                          key={cat.key}
                          label={cat.label}
                          value={ratings[cat.key]}
                          onChange={(v) => handleRatingChange(cat.key, v)}
                          index={index}
                        />
                      ))}
                    </Box>

                    {/* Noise + Sentiment */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2.5,
                        mb: 3,
                      }}
                    >
                      <motion.div
                        custom={5}
                        variants={fadeUp}
                        initial="hidden"
                        animate="visible"
                      >
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#9CA3AF',
                            mb: 1.25,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                        >
                          Noise Level
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 0.75,
                            bgcolor: '#F9FAFB',
                            borderRadius: '14px',
                            p: 0.75,
                            border: '1px solid #F3F4F6',
                          }}
                        >
                          {NOISE_OPTIONS.map((opt) => {
                            const Icon = opt.icon;
                            const selected =
                              ratings.noiseLevelsRating === opt.value;
                            return (
                              <Box
                                key={opt.value}
                                component={motion.button}
                                type="button"
                                whileTap={{ scale: 0.96 }}
                                onClick={() =>
                                  handleRatingChange(
                                    'noiseLevelsRating',
                                    opt.value,
                                  )
                                }
                                sx={{
                                  border: 'none',
                                  cursor: 'pointer',
                                  borderRadius: '10px',
                                  py: 1,
                                  px: 0.5,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  bgcolor: selected ? '#fff' : 'transparent',
                                  boxShadow: selected
                                    ? '0 2px 8px rgba(15, 23, 42, 0.08)'
                                    : 'none',
                                  color: selected ? ACCENT : '#9CA3AF',
                                  transition:
                                    'background-color 0.2s ease, color 0.2s ease',
                                }}
                              >
                                <Icon size={16} />
                                <Typography
                                  sx={{
                                    fontSize: '9px',
                                    fontWeight: 700,
                                    lineHeight: 1,
                                  }}
                                >
                                  {opt.label}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </motion.div>

                      <motion.div
                        custom={6}
                        variants={fadeUp}
                        initial="hidden"
                        animate="visible"
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: 700,
                              color: '#9CA3AF',
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}
                          >
                            Sentiment
                          </Typography>
                          <motion.span
                            key={sentimentEmoji}
                            initial={{ scale: 0.6, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{
                              type: 'spring',
                              stiffness: 400,
                              damping: 18,
                            }}
                            style={{ fontSize: 22 }}
                          >
                            {sentimentEmoji}
                          </motion.span>
                        </Box>
                        <Box
                          sx={{
                            bgcolor: '#F9FAFB',
                            borderRadius: '14px',
                            px: 2,
                            py: 1.5,
                            border: '1px solid #F3F4F6',
                          }}
                        >
                          <Slider
                            value={sentiment}
                            onChange={(_, v) => setSentiment(v)}
                            sx={{
                              color: ACCENT,
                              height: 6,
                              padding: '12px 0',
                              '& .MuiSlider-rail': {
                                opacity: 1,
                                bgcolor: '#E5E7EB',
                              },
                              '& .MuiSlider-thumb': {
                                width: 18,
                                height: 18,
                                bgcolor: ACCENT,
                                border: '3px solid #fff',
                                boxShadow: '0 2px 8px rgba(249, 115, 22, 0.35)',
                                transition: 'box-shadow 0.2s ease',
                                '&:hover, &.Mui-focusVisible': {
                                  boxShadow:
                                    '0 0 0 8px rgba(249, 115, 22, 0.15)',
                                },
                              },
                            }}
                          />
                        </Box>
                      </motion.div>
                    </Box>

                    {/* Thoughts */}
                    <motion.div
                      custom={7}
                      variants={fadeUp}
                      initial="hidden"
                      animate="visible"
                    >
                      <SectionTitle
                        action={
                          <Typography
                            sx={{
                              fontSize: '11px',
                              color: '#D1D5DB',
                            }}
                          >
                            Optional
                          </Typography>
                        }
                      >
                        Share Your Story
                      </SectionTitle>
                      <TextField
                        multiline
                        minRows={4}
                        fullWidth
                        placeholder="Tell us about your visit — what stood out?"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '14px',
                            bgcolor: '#FAFAFA',
                            fontSize: '14px',
                            transition:
                              'border-color 0.2s ease, box-shadow 0.2s ease',
                            '& fieldset': { borderColor: '#E5E7EB' },
                            '&:hover fieldset': { borderColor: '#D1D5DB' },
                            '&.Mui-focused fieldset': {
                              borderColor: ACCENT,
                              borderWidth: '1px',
                            },
                            '&.Mui-focused': {
                              boxShadow: '0 0 0 3px rgba(249, 115, 22, 0.12)',
                            },
                          },
                        }}
                      />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>

            {/* Footer CTA */}
            {!isReviewed && !isSuccessful && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.4 }}
                sx={{
                  px: { xs: 2.5, sm: 3 },
                  py: 2,
                  borderTop: '1px solid #F3F4F6',
                  bgcolor: '#fff',
                }}
              >
                <Button
                  fullWidth
                  disabled={!canSubmit}
                  onClick={handleSubmitRatings}
                  component={motion.button}
                  whileHover={canSubmit ? { scale: 1.01 } : {}}
                  whileTap={canSubmit ? { scale: 0.99 } : {}}
                  sx={{
                    py: 1.75,
                    borderRadius: '14px',
                    fontWeight: 800,
                    fontSize: '15px',
                    textTransform: 'none',
                    bgcolor: ACCENT,
                    color: '#fff',
                    boxShadow: canSubmit
                      ? '0 8px 24px rgba(249, 115, 22, 0.35)'
                      : 'none',
                    '&:hover': {
                      bgcolor: '#EA580C',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#F3F4F6',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  Post My Review
                </Button>
              </Box>
            )}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Rating;
